import { useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Copy, FileText, Pencil, PlayCircle, RefreshCw, AlertTriangle, Loader2, ListChecks } from 'lucide-react';
import type { Block, Message } from '../../types/api';
import { AssistantContent } from './AssistantContent';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import type { LiveTurn } from './useRun';
import { Button, IconButton } from '../../components/ui';
import { withAuth, API_URL } from '../../lib/api';
import { bytes } from '../../lib/format';
import { tierName } from '../../lib/tiers';

export interface MessageActions {
  onEdit: (m: Message, text: string) => void;
  onRegenerate: (m: Message) => void;
  onContinue: () => void;
  onBranch: (messageId: string) => void;
  onAnswer: (text: string) => void;
}

function plainText(blocks: Block[]) {
  return blocks.filter((b) => b.type === 'text').map((b) => b.text).join('\n\n');
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <IconButton size="sm" label={done ? 'Copied' : 'Copy'} onClick={() => { void navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }}>
      {done ? <Check size={14} /> : <Copy size={14} />}
    </IconButton>
  );
}

function Siblings({ m, onBranch }: { m: Message; onBranch: (id: string) => void }) {
  const { ids, index } = m.siblings;
  if (ids.length < 2) return null;
  return (
    <span className="siblings">
      <IconButton size="sm" label="Previous version" disabled={index === 0} onClick={() => onBranch(ids[index - 1])}><ChevronLeft size={14} /></IconButton>
      <span>{index + 1} / {ids.length}</span>
      <IconButton size="sm" label="Next version" disabled={index === ids.length - 1} onClick={() => onBranch(ids[index + 1])}><ChevronRight size={14} /></IconButton>
    </span>
  );
}

export function UserMessage({ m, actions, busy }: { m: Message; actions: MessageActions; busy: boolean }) {
  const blocks = m.blocks.map((b) => b.data);
  const text = plainText(blocks);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(text);
  const attachments = blocks.filter((b) => b.type === 'attachment');
  return (
    <div className="msg msg--user" data-testid="user-message">
      {attachments.length > 0 && (
        <div className="msg__attachments">
          {attachments.map((a) => a.kind === 'image'
            ? <img key={a.fileId} className="msg__image" src={withAuth(`${API_URL}/api/files/${a.fileId}/content?inline=1`)} alt={a.filename} />
            : <a key={a.fileId} className="attach attach--static" href={withAuth(`${API_URL}/api/files/${a.fileId}/content`)} target="_blank" rel="noreferrer"><span className="attach__icon"><FileText size={16} /></span><span className="attach__meta"><span className="attach__name">{a.filename}</span><span className="attach__sub">{bytes(a.sizeBytes ?? 0)}</span></span></a>)}
        </div>
      )}
      {editing ? (
        <div className="msg__edit">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus rows={Math.min(10, draft.split('\n').length + 1)} aria-label="Edit message" />
          <div className="msg__edit-actions">
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setDraft(text); }}>Cancel</Button>
            <Button size="sm" variant="primary" disabled={!draft.trim() || busy} onClick={() => { setEditing(false); actions.onEdit(m, draft.trim()); }}>Save &amp; send</Button>
          </div>
        </div>
      ) : (
        text && <div className="msg__bubble">{text}</div>
      )}
      {!editing && (
        <div className="msg__actions">
          <Siblings m={m} onBranch={actions.onBranch} />
          <CopyButton text={text} />
          <IconButton size="sm" label="Edit message" disabled={busy} onClick={() => { setDraft(text); setEditing(true); }}><Pencil size={14} /></IconButton>
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  queued: 'Starting', planning: 'Planning', running: 'Working', executing_tools: 'Using tools', validating: 'Testing output', revising: 'Fixing problems', waiting_for_authorization: 'Waiting for approval',
};

export function AssistantMessage({ m, live, actions, isLast, busy }: { m: Message; live: LiveTurn | null; actions: MessageActions; isLast: boolean; busy: boolean }) {
  const isLive = !!live && live.assistantMessageId === m.id && !live.done;
  const blocks: Block[] = isLive ? Object.keys(live!.blocks).map(Number).sort((a, b) => a - b).map((k) => live!.blocks[k]) : m.blocks.map((b) => b.data);
  const text = plainText(blocks);
  const status = isLive ? 'streaming' : m.status;
  const liveTools = isLive ? Object.entries(live!.tools) : [];
  const currentTool = liveTools.filter(([, tool]) => tool.status === 'running').at(-1)?.[1];
  const completedTools = liveTools.filter(([, tool]) => tool.status === 'done').length;
  const liveStatus = currentTool?.activity ?? (STATUS_LABEL[live?.status ?? ''] ?? 'Working');
  const canContinue = !isLive && isLast && (m.status === 'cancelled' || ['max_tokens', 'max_iterations', 'budget_exhausted'].includes(m.stopReason ?? ''));
  return (
    <div className="msg msg--assistant" data-testid="assistant-message">
      <ErrorBoundary><AssistantContent blocks={blocks} tools={isLive ? live!.tools : {}} liveArtifacts={isLive ? live!.artifacts : []} running={isLive} onAnswer={!isLive && isLast && !busy ? actions.onAnswer : undefined} /></ErrorBoundary>
      {isLive && (
        <div className="live-activity" role="status" aria-live="polite" aria-atomic="true">
          <Loader2 size={15} className="spin" aria-hidden="true" />
          <span className="live-activity__label">{liveStatus}{live!.statusDetail ? ` · ${live!.statusDetail}` : ''}</span>
          {live!.usage?.billing && <span className="muted small" title="Credits deducted for provider-reported usage so far. More output and separate media charges can follow.">{live!.usage.billing.chargedCredits!==undefined?`${Number(live!.usage.billing.chargedCredits).toFixed(3)} token credits deducted so far`:`${Number(live!.usage.billing.rawCredits).toFixed(3)} reported token credits · debit pending`}</span>}
          {liveTools.length > 0 && <span className="live-activity__count"><ListChecks size={13} /> {completedTools}/{liveTools.length} steps</span>}
        </div>
      )}
      {!isLive && m.status === 'error' && m.stopReason !== 'max_tokens' && (
        <div className="msg__error" role="alert"><AlertTriangle size={15} /><span>{m.error ?? 'Something went wrong.'}</span><Button size="sm" onClick={() => actions.onRegenerate(m)} disabled={busy} icon={<RefreshCw size={13} />}>Retry</Button></div>
      )}
      {!isLive && m.usage?.billing && <p className="muted small">{Number(m.usage.billing.chargedCredits).toFixed(3)} token credits charged{m.usage.billing.discountCredits>0?` · ${Number(m.usage.billing.discountCredits).toFixed(3)} cache discount`:''}</p>}
      {!isLive && m.status === 'cancelled' && <p className="muted small">Stopped.</p>}
      {!isLive && status !== 'streaming' && (
        <div className="msg__actions msg__actions--assistant">
          <Siblings m={m} onBranch={actions.onBranch} />
          {text && <CopyButton text={text} />}
          <IconButton size="sm" label="Regenerate" disabled={busy} onClick={() => actions.onRegenerate(m)}><RefreshCw size={14} /></IconButton>
          {canContinue && <Button size="sm" variant="ghost" icon={<PlayCircle size={14} />} disabled={busy} onClick={actions.onContinue}>Continue</Button>}
          {m.usage?.output_tokens ? <span className="msg__meta">{tierName(m.model)} · {m.usage.input_tokens + m.usage.output_tokens} tokens</span> : null}
        </div>
      )}
    </div>
  );
}
